import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, DeviceEntry, DeviceType, NewPairingCode } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";

export const DEVICE_TYPES: DeviceType[] = ["pc", "sunmi", "iphone", "ipad", "android", "tablet"];

/** "Kassa PC 2", "iPhone 1" (prototype defName): the kind and the next number. */
const NAMES: Record<DeviceType, string> = {
  pc: "Kassa PC",
  sunmi: "Sunmi",
  iphone: "iPhone",
  ipad: "iPad",
  android: "Gsm",
  tablet: "Tablet",
};

/** The default name of a new device: its kind and the next number among the devices of that kind. */
export function defaultDeviceName(type: DeviceType, devices: readonly { type: string }[]): string {
  return `${NAMES[type]} ${String(devices.filter((device) => device.type === type).length + 1)}`;
}

interface Props {
  authorization: Authorization;
  devices: readonly DeviceEntry[];
  /** From Personeel: the phone of this person, "Gsm Yusuf" (Furkan, 09/10/2026). */
  initial?: { type: DeviceType; name: string };
  onClose: () => void;
}

/**
 * "Nieuw toestel" (prototype openDeviceSetup, besluiten: paired once by the owner, then recognised): the
 * kind and a name, then a one-time code of 15 minutes to type on the new device. A kassa-pc becomes a
 * till; a Sunmi, phone or tablet the Tafo app, which chooses ober or koerier itself (blok J).
 */
export function NewDeviceDialog({ authorization, devices, initial, onClose }: Props) {
  const { api } = useTill();
  const text = t.printing.newDevice;
  const nameFor = (type: DeviceType) => defaultDeviceName(type, devices);
  const [type, setType] = useState<DeviceType>(initial?.type ?? "pc");
  const [name, setName] = useState(() => initial?.name ?? nameFor("pc"));
  const [made, setMade] = useState<NewPairingCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const make = async () => {
    setBusy(true);
    setError(null);
    try {
      setMade(
        await api.request<NewPairingCode>("POST", "/device/pairing-codes", {
          staffToken: authorization.token,
          body: { name: name.trim() || nameFor(type), type },
        }),
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
    } finally {
      setBusy(false);
    }
  };

  if (made !== null) {
    return (
      <Dialog
        title={text.title}
        onClose={onClose}
        footer={
          <button type="button" className="btn primary" onClick={onClose}>
            {text.done}
          </button>
        }
      >
        <p className="newcode num" aria-label={text.codeLabel}>
          {made.code}
        </p>
        <p className="note">{text.valid(formatTime(made.expiresAt))}</p>
        <p>
          {made.purpose === "till"
            ? text.whereTill(window.location.origin)
            : text.whereApp(`${window.location.origin}/app`)}
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={text.title}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t.confirm.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void make()}>
            {text.make}
          </button>
        </>
      }
    >
      <div className="grp">
        <h3>{text.which}</h3>
        <div className="chips">
          {DEVICE_TYPES.map((kind) => (
            <button
              key={kind}
              type="button"
              className="chip"
              aria-pressed={type === kind}
              onClick={() => {
                // a name that was still the default follows the kind
                if (name === nameFor(type)) setName(nameFor(kind));
                setType(kind);
              }}
            >
              {t.printing.deviceTypes[kind]}
            </button>
          ))}
        </div>
      </div>
      <label className="flabel" htmlFor="newDeviceName">
        {text.name}
      </label>
      <input
        id="newDeviceName"
        value={name}
        maxLength={60}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      <p className="note">{text.info[type]}</p>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
